import { Component, ElementRef, AfterViewInit, ViewChild, OnDestroy, Input } from '@angular/core';
import { VIDEO_TARGET, VideoOverlay, VideoOverlayService } from './video-overlay.service';
import { Observable, Subscription } from 'rxjs';
import { LoggingService, LogSeverity } from '@core/services/logging.service';

@Component({
  selector: 'app-video-overlay',
  templateUrl: './video-overlay.component.html',
  styleUrls: ['./video-overlay.component.scss'],
  standalone: false,
})
export class VideoOverlayComponent implements AfterViewInit, OnDestroy {
  @ViewChild('videoParent') parentElem!: ElementRef;
  @Input() id: number = VIDEO_TARGET.Global;

  private overlayTrigger: Observable<VideoOverlay | undefined>;
  private unsubOverlayTrigger: Subscription | undefined;
  private currentVideoOverlay: VideoOverlay | undefined;

  constructor(private videoOverlayService: VideoOverlayService, private logger: LoggingService) {
    this.overlayTrigger = this.videoOverlayService.getTrigger();
  }

  ngAfterViewInit() {
    this.unsubOverlayTrigger = this.overlayTrigger.subscribe((videoOverlay: VideoOverlay | undefined) => {
      if (videoOverlay && videoOverlay.targetId === this.id) {
        if (videoOverlay.src) {
          // A src has been provided, start the overlay
          this.startVideoOverlay(videoOverlay);
        } else {
          // No src provided, stop any existing overlay
          this.stopVideoOverlay();
        }
      } else {
        // No overlay provided, stop any existing overlay
        this.stopVideoOverlay();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.unsubOverlayTrigger) {
      this.unsubOverlayTrigger.unsubscribe();
    }
  }

  private startVideoOverlay(videoOverlay: VideoOverlay) {
    this.currentVideoOverlay = videoOverlay;

    const parentDiv = this.parentElem.nativeElement as HTMLDivElement;
    parentDiv.style.display = 'block';
    parentDiv.style.background = 'transparent';
    const videoElem: HTMLVideoElement = document.createElement('video');
    videoElem.style.width = '100%';
    videoElem.style.height = '100%';
    videoElem.style.objectFit = 'fill';

    videoElem.muted = true;
    videoElem.autoplay = videoOverlay.delayStartMs ? false : true;
    videoElem.loop = this.currentVideoOverlay?.loop || false;
    videoElem.src = this.currentVideoOverlay?.src || '';

    parentDiv.appendChild(videoElem);

    this.logger.writeLog(`[VideoOverlayComponent:startVideoOverlay] Video overlay started for ${videoElem.src}`, {
      delay: this.currentVideoOverlay?.delayRemovalMs,
      loop: this.currentVideoOverlay?.loop,
      severity: LogSeverity.DEBUG,
    });

    setTimeout(() => {
      videoElem.play();
    }, videoOverlay.delayStartMs);

    // Auto remove element off the page if it's not meant to loop
    if (!this.currentVideoOverlay?.loop) {
      videoElem.addEventListener('ended', () => {
        this.logger.writeLog(`[VideoOverlayComponent:startVideoOverlay] Video overlay ended for ${videoElem.src}`, {
          delay: this.currentVideoOverlay?.delayRemovalMs,
          loop: this.currentVideoOverlay?.loop,
          severity: LogSeverity.DEBUG,
        });
        // Delay removal if we feel the src video ends too quick
        setTimeout(() => {
          parentDiv.removeChild(videoElem);
          parentDiv.style.display = 'none';
          if (this.currentVideoOverlay?.callback) {
            this.currentVideoOverlay.callback();
          }
        }, this.currentVideoOverlay?.delayRemovalMs);
      });
      if (this.currentVideoOverlay?.stopVideoEarlyMs) {
        // Set a timeout to stop the video early
        setTimeout(() => {
          this.stopVideoOverlay();
        }, this.currentVideoOverlay.stopVideoEarlyMs);
      }
    }
  }

  private stopVideoOverlay() {
    if (this.currentVideoOverlay) {
      const parentDiv = this.parentElem.nativeElement as HTMLDivElement;
      parentDiv.style.display = 'none';
      parentDiv.innerHTML = '';
      this.logger.writeLog(`[VideoOverlayComponent:stopVideoOverlay] Video overlay stopped`, {
        severity: LogSeverity.DEBUG,
      });
      if (this.currentVideoOverlay.callback) {
        this.currentVideoOverlay.callback();
      }
      this.currentVideoOverlay = undefined;
    }
  }
}
